import React, { useId, useMemo } from 'react';
import type { DailyAverage, DailyCount } from '../../types/platformStats';
import { fillDailyAverages, fillDailyCounts, formatShortDate } from '../../utils/statsChartData';

export interface ChartSeries {
  label: string;
  color: string;
  data: DailyCount[];
}

interface StatsTrendChartProps {
  title: string;
  series?: ChartSeries[];
  variant?: 'bar' | 'line';
  lineData?: DailyAverage[];
  lineLabel?: string;
  formatValue?: (value: number) => string;
  emptyLabel?: string;
}

const CHART_WIDTH = 640;
const CHART_HEIGHT = 200;
const PAD = { top: 12, right: 8, bottom: 28, left: 40 };

export const StatsTrendChart: React.FC<StatsTrendChartProps> = ({
  title,
  series = [],
  variant = 'bar',
  lineData,
  lineLabel,
  formatValue = (v) => String(Math.round(v)),
  emptyLabel = '—',
}) => {
  const gradientId = useId().replace(/:/g, '');
  const plotW = CHART_WIDTH - PAD.left - PAD.right;
  const plotH = CHART_HEIGHT - PAD.top - PAD.bottom;

  const lineFilled = useMemo(
    () => (lineData ? fillDailyAverages(lineData) : []),
    [lineData]
  );

  const filledSeries = useMemo(
    () => series.map((s) => ({ ...s, data: fillDailyCounts(s.data) })),
    [series]
  );

  const labels =
    variant === 'line'
      ? lineFilled.map((d) => d.date)
      : (filledSeries[0]?.data.map((d) => d.date) ?? []);

  const maxValue = useMemo(() => {
    if (variant === 'line') {
      return Math.max(1, ...lineFilled.map((d) => d.value));
    }
    return Math.max(1, ...filledSeries.flatMap((s) => s.data.map((d) => d.count)));
  }, [variant, lineFilled, filledSeries]);

  const hasData = useMemo(() => {
    if (variant === 'line') {
      return lineFilled.some((d) => d.value > 0);
    }
    return filledSeries.some((s) => s.data.some((d) => d.count > 0));
  }, [variant, lineFilled, filledSeries]);

  const tickIndices = useMemo(() => {
    if (labels.length <= 7) {
      return labels.map((_, i) => i);
    }
    const step = Math.ceil(labels.length / 6);
    const indices: number[] = [];
    for (let i = 0; i < labels.length; i += step) {
      indices.push(i);
    }
    if (indices[indices.length - 1] !== labels.length - 1) {
      indices.push(labels.length - 1);
    }
    return indices;
  }, [labels]);

  const xStep = labels.length > 1 ? plotW / (labels.length - 1) : plotW;
  const yScale = (v: number) => PAD.top + plotH - (v / maxValue) * plotH;

  if (!hasData) {
    return (
      <div className="admin-stats-chart">
        <h3 className="admin-stats-chart__title">{title}</h3>
        <p className="admin-stats-chart__empty">{emptyLabel}</p>
      </div>
    );
  }

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const y = PAD.top + plotH * (1 - t);
    const val = maxValue * t;
    return (
      <g key={t}>
        <line
          x1={PAD.left}
          x2={CHART_WIDTH - PAD.right}
          y1={y}
          y2={y}
          className="admin-stats-chart__grid"
        />
        <text x={PAD.left - 6} y={y + 4} className="admin-stats-chart__axis-y" textAnchor="end">
          {formatValue(val)}
        </text>
      </g>
    );
  });

  const xLabels = tickIndices.map((i) => (
    <text
      key={labels[i]}
      x={
        variant === 'line'
          ? PAD.left + i * xStep
          : PAD.left + i * (plotW / labels.length) + plotW / labels.length / 2
      }
      y={CHART_HEIGHT - 6}
      className="admin-stats-chart__axis-x"
      textAnchor="middle"
    >
      {formatShortDate(labels[i])}
    </text>
  ));

  if (variant === 'line') {
    const points = lineFilled
      .map((d, i) => `${PAD.left + i * xStep},${yScale(d.value)}`)
      .join(' ');

    return (
      <div className="admin-stats-chart">
        <h3 className="admin-stats-chart__title">{title}</h3>
        {lineLabel && (
          <div className="admin-stats-chart__legend">
            <span className="admin-stats-chart__legend-item">
              <span
                className="admin-stats-chart__legend-swatch"
                style={{ background: 'var(--color-primary)' }}
              />
              {lineLabel}
            </span>
          </div>
        )}
        <svg
          className="admin-stats-chart__svg"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          role="img"
          aria-label={title}
        >
          {gridLines}
          <polyline
            points={points}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {lineFilled.map((d, i) => (
            <circle
              key={d.date}
              cx={PAD.left + i * xStep}
              cy={yScale(d.value)}
              r={3}
              fill="var(--color-primary)"
            />
          ))}
          {xLabels}
        </svg>
      </div>
    );
  }

  const groupCount = filledSeries.length;
  const barSlot = labels.length > 0 ? plotW / labels.length : plotW;
  const barWidth = Math.max(2, (barSlot * 0.72) / groupCount);
  const groupOffset = (barSlot - barWidth * groupCount) / 2;

  return (
    <div className="admin-stats-chart">
      <h3 className="admin-stats-chart__title">{title}</h3>
      <div className="admin-stats-chart__legend">
        {filledSeries.map((s) => (
          <span key={s.label} className="admin-stats-chart__legend-item">
            <span
              className="admin-stats-chart__legend-swatch"
              style={{ background: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>
      <svg
        className="admin-stats-chart__svg"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        role="img"
        aria-label={title}
      >
        <defs>
          {filledSeries.map((s, si) => (
            <linearGradient
              key={s.label}
              id={`${gradientId}-bar-${si}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={s.color} stopOpacity={0.95} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.55} />
            </linearGradient>
          ))}
        </defs>
        {gridLines}
        {filledSeries.map((s, si) =>
          s.data.map((d, di) => {
            const x = PAD.left + di * barSlot + groupOffset + si * barWidth;
            const barH = (d.count / maxValue) * plotH;
            const y = PAD.top + plotH - barH;
            return (
              <rect
                key={`${s.label}-${d.date}`}
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barH, d.count > 0 ? 2 : 0)}
                rx={2}
                fill={`url(#${gradientId}-bar-${si})`}
              >
                <title>
                  {formatShortDate(d.date)}: {d.count} {s.label}
                </title>
              </rect>
            );
          })
        )}
        {xLabels}
      </svg>
    </div>
  );
};
